/**
 * ThemeApplier — web-only side effect that mirrors the resolved theme mode
 * onto <html data-theme> and the root `color-scheme`. The shared ThemeProvider
 * owns the state (and persistence); this is the DOM bridge the CSS in
 * tokens.css responds to. Rendered once, inside <ThemeProvider> and inside the
 * router, because the affiliate portal opts out of the member theme.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@wlb/shared/theme';
import { isAffiliateSurfacePath } from '../../features/affiliate/affiliateRoutes';

export function ThemeApplier(): null {
  const { mode, preferenceLoaded } = useTheme();
  const { pathname } = useLocation();
  const lightOnly = isAffiliateSurfacePath(pathname);

  useEffect(() => {
    // The inline script in index.html already applied the stored preference
    // before first paint. Until storage resolves, `mode` is only the OS
    // default, so writing it here would undo that and cause the very flash
    // the script prevents. A light-only surface has nothing to wait for: its
    // answer does not depend on the preference.
    if (!lightOnly && !preferenceLoaded) return;
    const root = document.documentElement;
    const applied = lightOnly ? 'light' : mode;
    root.setAttribute('data-theme', applied);
    // An inline `color-scheme` is what the native controls, scrollbars and form
    // widgets follow, and it outranks the stylesheet, so the affiliate pages
    // need it reset here too or their inputs stay dark on a light page.
    root.style.colorScheme = applied;
  }, [lightOnly, mode, preferenceLoaded]);

  return null;
}
